/**
 * Whether the device registry said this session was signed out (Manage
 * devices / User management → Sign out a device). Push registration asks
 * before sending a token, and waits while a registry check is in flight, so a
 * signed-out device never puts its alert token back in the moment before it
 * signs itself out. Only a "signed out" answer blocks: a slow or failed check
 * never strands push.
 */

const WAIT_LIMIT_MS = 15_000

let generation = 0
let checksInFlight = 0
let signedOut = false
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of [...listeners]) listener()
}

/** A registry check started; call the returned function once with its answer. */
export function beginDeviceSessionCheck(): (answer: 'live' | 'signed-out') => void {
  const startedIn = generation
  checksInFlight += 1
  let ended = false
  return (answer) => {
    if (ended || startedIn !== generation) return
    ended = true
    checksInFlight -= 1
    if (answer === 'signed-out') signedOut = true
    notify()
  }
}

/** A different account (or nobody) is signed in now: forget the last answer. */
export function resetDeviceSessionCheck(): void {
  generation += 1
  checksInFlight = 0
  signedOut = false
  notify()
}

/**
 * Resolves `true` when this session may register push: no registry check is
 * in flight and none said "signed out". Gives up waiting after `timeoutMs`.
 */
export function waitForDeviceSessionCheck(timeoutMs = WAIT_LIMIT_MS): Promise<boolean> {
  return new Promise((resolve) => {
    const finish = () => {
      listeners.delete(check)
      clearTimeout(timer)
      resolve(!signedOut)
    }
    function check() {
      if (signedOut || checksInFlight === 0) finish()
    }
    listeners.add(check)
    const timer = setTimeout(finish, timeoutMs)
    check()
  })
}
